import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { isPresent, hasItems } from "./helpers";
import Carousel from "./Carousel";
import { useEffect, useState } from "react";

export default function TransformationsPreview({ section, transformations }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  if (!section || !hasItems(transformations)) return null;

  const items = transformations.filter(
    (item) => isPresent(item.before_image) && isPresent(item.after_image),
  );

  const Card = (item, i) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.1 }}
      className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] overflow-hidden group"
    >
      <div className="relative h-64 overflow-hidden flex">
        <div className="w-1/2 relative">
          <img
            src={item.before_image}
            alt="Before"
            className="w-full h-full object-cover"
          />
          <span className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 font-oswald uppercase">
            Before
          </span>
        </div>
        <div className="w-1/2 relative">
          <img
            src={item.after_image}
            alt="After"
            className="w-full h-full object-cover"
          />
          <span className="absolute bottom-2 right-2 bg-[var(--dvx-primary)] text-white text-xs px-2 py-1 font-oswald uppercase">
            After
          </span>
        </div>
      </div>

      <div className="p-4">
        {isPresent(item.name) && (
          <h4 className="font-oswald text-[var(--dvx-text)] font-bold uppercase">
            {item.name}
          </h4>
        )}

        {(isPresent(item.program) || isPresent(item.duration)) && (
          <div className="flex items-center gap-2 mt-2 text-sm">
            {isPresent(item.program) && (
              <span className="text-[var(--dvx-primary)]">{item.program}</span>
            )}
            {isPresent(item.program) && isPresent(item.duration) && (
              <span className="text-[var(--dvx-text-muted)]">â€¢</span>
            )}
            {isPresent(item.duration) && (
              <span className="text-[var(--dvx-text-muted)]">
                {item.duration}
              </span>
            )}
          </div>
        )}

        {isPresent(item.weight_lost) && (
          <div className="mt-2 text-sm text-[var(--dvx-text-muted)]">
            Lost:{" "}
            <span className="text-[var(--dvx-text)]">{item.weight_lost}</span>
          </div>
        )}

        {isPresent(item.muscle_gained) && (
          <div className="mt-2 text-sm text-[var(--dvx-text-muted)]">
            Gained:{" "}
            <span className="text-[var(--dvx-text)]">{item.muscle_gained}</span>
          </div>
        )}
        <p className="mt-4 text-[var(--dvx-text-muted)] italic">
          "{item.testimonial}"
        </p>
      </div>
    </motion.div>
  );

  return (
    <section
      data-testid="transformations-preview"
      id="transformations"
      className="py-24 md:py-32 bg-[var(--dvx-bg)] transition-colors duration-300 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {isPresent(section.badge) && (
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
              {section.badge}
            </h3>
          )}
          {isPresent(section.title) && (
            <h2 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {section.title}
            </h2>
          )}
        </motion.div>

        {/* MOBILE â†’ CAROUSEL */}
        {isMobile ? (
          <Carousel
            items={items}
            itemsPerPage={1}
            autoplayMs={3500}
            pauseOnHover={true}
            renderItem={(item, i) => Card(item, i)}
          />
        ) : (
          /* DESKTOP â†’ CAROUSEL (4 PER SLIDE) */
          <Carousel
            items={items}
            itemsPerPage={4}
            autoplayMs={7500}
            pauseOnHover={true}
            gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
            renderItem={(item, i) => Card(item, i)}
          />
        )}

        {/* BOTTOM SECTION */}
        <div className="mt-12 flex flex-col lg:flex-row items-stretch gap-6">
          <div className="lg:w-[30%] flex items-center">
            <Link to="/transformations" className="w-full">
              <Button
                variant="outline"
                className="w-full rounded-none px-6 py-6 border-[var(--dvx-primary)] text-[var(--dvx-primary)] font-oswald font-bold uppercase tracking-widest hover:bg-[var(--dvx-primary)] hover:text-white transition-all"
              >
                View All Transformations
              </Button>
            </Link>
          </div>

          <div className="lg:w-[70%] border border-[var(--dvx-border)] bg-[var(--dvx-surface)] py-3 px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="font-oswald text-lg font-bold uppercase text-[var(--dvx-text)] mb-1">
                Want an event-based transformation?
              </h3>
              <p className="text-sm text-[var(--dvx-text-muted)] max-w-md">
                Wedding / photoshoot / vacation timeline. Apply and weâ€™ll map a
                realistic plan with milestones.
              </p>
            </div>

            <Link to="/contact">
              <Button className="rounded-none px-6 py-3 bg-[var(--dvx-primary)] text-white font-oswald font-bold uppercase hover:opacity-90 transition">
                Apply with timeline
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
